<template>
    <div class="local-login">
        <div class="local-login__card">
            <h1 class="local-login__title">hLiberty · ata</h1>

            <template v-if="!local">
                <p class="local-login__text">
                    {{ $t('components.ata.localLogin.adminOnly') }}
                </p>
                <v-btn color="primary" variant="flat" block @click="goToAdmin">
                    {{ $t('common.labels.backToAdmin') }}
                </v-btn>
            </template>

            <template v-else>
                <p class="local-login__text">
                    {{ $t('components.ata.localLogin.explanation') }}
                </p>

                <v-alert type="warning" variant="tonal" density="compact" class="mb-4">
                    {{ $t('components.ata.localLogin.warning') }}
                </v-alert>

                <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
                    {{ error }}
                </v-alert>

                <v-form @submit.prevent="signIn">
                    <v-text-field
                        v-model="userId"
                        label="user_id"
                        density="compact"
                        variant="outlined"
                        class="mb-2"
                        autofocus
                        required />
                    <v-text-field
                        v-model="corp"
                        label="corp"
                        density="compact"
                        variant="outlined"
                        class="mb-3"
                        required />
                    <v-btn
                        type="submit"
                        color="primary"
                        variant="flat"
                        block
                        :loading="loading"
                        :disabled="!userId || !corp">
                        Entrar
                    </v-btn>
                </v-form>
            </template>
        </div>
    </div>
</template>

<script setup>
/**
 * Entrada local de desenvolvimento (rota `/entrar` -- o caminho fica em portugues de proposito: outros apps o usam
 * como destino de retorno do SSO).
 *
 * Em ambiente publicado esta tela nao cria sessao nenhuma: ela so explica que
 * a entrada e pelo admin e oferece o caminho de volta. O formulario so
 * aparece quando **os dois lados** estao em modo local -- o /config desta
 * aplicacao e o ATA_LOCAL_LOGIN da API. Sem isso o endpoint responde 404.
 *
 * Diferente do deck (de onde este molde veio), a API do ata nao semeia uma
 * lista de pessoas para escolher -- `POST /api/dev/login` aceita qualquer
 * `user_id` (ver DevTokenIssuer no ata-api), entao a tela e so um
 * formulario de dois campos.
 *
 * O token que chega aqui e emitido pela API do ata com o mesmo formato
 * do liberty/auth (ver PlatformToken/DevTokenIssuer): assim o caminho
 * exercitado na maquina e o mesmo que roda no cluster, menos a origem do
 * token.
 */
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { setAuth } from '@/services/api/auth.js';
import { localLogin } from '@/services/api/ata/session.js';
import { backToAdmin, DEFAULT_DESTINATION } from '@/services/sso.js';

const router = useRouter();
const route = useRoute();

const local = ref(!!window.LOCAL_LOGIN);
const userId = ref('');
const corp = ref('local');
const loading = ref(false);
const error = ref('');

function goToAdmin() {
    backToAdmin({ reason: 'sem-sessao' });
}

async function signIn() {
    loading.value = true;
    error.value = '';

    try {
        const response = await localLogin({ userId: userId.value, corp: corp.value });

        setAuth({
            token: response.access_token,
            // O /me do liberty/auth nao existe no modo local: monta-se aqui o
            // minimo que `getUser()` le, a partir do que foi digitado.
            credentials: {
                user: { id: userId.value, login: userId.value, name: userId.value, corps: {} },
                corp: corp.value,
            },
        });

        localStorage.setItem('corp', corp.value);

        await router.replace(route.query.from || DEFAULT_DESTINATION);
    } catch (e) {
        error.value = e?.response?.data?.message
            || 'A API do ata nao esta com o login local ligado (ATA_LOCAL_LOGIN).';
    } finally {
        loading.value = false;
    }
}
</script>

<style scoped lang="scss">
.local-login {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f4f5f8;
    padding: 24px;

    &__card {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, .08);
        padding: 28px;
        width: 100%;
        max-width: 420px;
    }

    &__title {
        font-size: 18px;
        margin: 0 0 6px;
    }

    &__text {
        color: var(--secondaryColor);
        font-size: 14px;
        margin: 0 0 16px;
    }
}
</style>
